import axiosInstance from './AxiosService';

const policyService = {
    // Fetch all policies
    fetchPolicies: async () => {
        try {
            const response = await axiosInstance.get('policies/');
            return response.data;
        } catch (error) {
            console.error("Error fetching policies:", error.response?.data || error.message);
            throw error; // Re-throw the error to be handled by the calling component
        }
    },

    // Fetch a single policy by ID (example, might not be needed currently)
    fetchPolicyById: async (policyId) => {
        try {
            const response = await axiosInstance.get(`policies/${policyId}/`);
            return response.data;
        } catch (error) {
            console.error(`Error fetching policy ${policyId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // Create a new policy (handles FormData for file uploads)
    createPolicy: async (formData) => {
        try {
            // Use axiosInstance but override Content-Type for this specific request
            // Axios automatically sets the correct multipart/form-data header when data is FormData
            // *if* the default Content-Type is not forced in the instance config.
            // Since we reverted AxiosService, we MUST explicitly remove the default JSON header here.
            const response = await axiosInstance.post('policies/', formData, {
                headers: {
                    // Explicitly set Content-Type to undefined/null or let Axios handle it.
                    // Setting it to null/undefined might be necessary to force Axios to re-evaluate.
                    // Alternatively, directly setting 'multipart/form-data' might work, 
                    // but letting Axios handle the boundary is usually preferred.
                    // Let's try letting Axios handle it by removing the instance default:
                    'Content-Type': null // Or try 'multipart/form-data' if null doesn't work
                }
            });
            return response.data;
        } catch (error) {
            console.error("Error creating policy:", error.response?.data || error.message);
            throw error;
        }
    },

    // Update an existing policy (example - adapt if file uploads are needed)
    updatePolicy: async (policyId, formData) => {
        // If updates can include files, use FormData and similar header logic as createPolicy
        try {
            const response = await axiosInstance.put(`policies/${policyId}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            return response.data;
        } catch (error) {
            console.error(`Error updating policy ${policyId}:`, error.response?.data || error.message);
            throw error;
        }
    },

    // Delete a policy
    deletePolicy: async (policyId) => {
        try {
            const response = await axiosInstance.delete(`policies/${policyId}/`);
            // Delete often returns 204 No Content, so check for that
            return response.status === 204 ? { success: true } : response.data;
        } catch (error) {
            console.error(`Error deleting policy ${policyId}:`, error.response?.data || error.message);
            throw error;
        }
    },
    
    // Create a new password policy for a company
    createPasswordPolicy: async (passwordPolicyData) => {
        try {
            const response = await axiosInstance.post('policy-passwords/', passwordPolicyData);
            return response.data;
        } catch (error) {
            console.error("Error creating password policy:", error.response?.data || error.message);
            throw error;
        }
    },
    
    // Get password policy for a specific company
    getPasswordPolicyByCompany: async (companyId) => {
        try {
            const response = await axiosInstance.get(`policy-passwords/company/${companyId}/`);
            return response.data;
        } catch (error) {
            if (error.response && error.response.status === 404) {
                return null; // Return null if policy doesn't exist
            }
            console.error(`Error fetching password policy for company ${companyId}:`, error.response?.data || error.message);
            throw error;
        }
    },
    
    // Update an existing password policy
    updatePasswordPolicy: async (policyId, passwordPolicyData) => {
        try {
            const response = await axiosInstance.put(`policy-passwords/${policyId}/`, passwordPolicyData);
            return response.data;
        } catch (error) {
            console.error(`Error updating password policy ${policyId}:`, error.response?.data || error.message);
            throw error;
        }
    }
};

export default policyService; 